'use client';

import { AddCreditsModal } from '@components/CreditUsage/AddCreditsModal';
import { CreditUsageMenuItem } from '@components/CreditUsage/CreditUsageMenuItem';
import { CreditUsageOverlay } from '@components/CreditUsage/CreditUsageOverlay';
import { useCreditUsageSecretAccess } from '@components/CreditUsage/secret-access';
import { WORKSPACE_ROUTES } from '@components/SideNav2/constants';
import { useSideNav } from '@components/SideNav2/context';
import type { WorkspacePlanType, WorkspaceRole, WorkspaceType } from '@components/SideNav2/types';
import { Avatar, Box, Divider, Group, Menu, Stack, Text } from '@mantine/core';
import { useFeatureFlag } from '@modules/feature-flag';
import { useGlobalModal } from '@modules/global-modals/use-global-modal';
import { useNavigationInterceptor } from '@modules/navigation-guard/use-navigation-guard';
import Link from 'next/link';
import { useState } from 'react';

import { ContractedButton } from './components/ContractedButton/ContractedButton';
import { ExpandedButton } from './components/ExpandedButton/ExpandedButton';
import { dropDownContainer } from './styles.css';

interface DropdownProps {
  workspaceId: string;
  workspaceType: WorkspaceType;
  workspaceAvatarUrl?: string;
  workspaceName: string;
  workspaceRole: WorkspaceRole;
  planDescription: string;
  planType: WorkspacePlanType;
}

export function Dropdown({
  workspaceId,
  workspaceType,
  workspaceName,
  planDescription,
  workspaceAvatarUrl,
  planType,
  workspaceRole,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [usageOpen, setUsageOpen] = useState<boolean>(false);
  const [addCreditsOpen, setAddCreditsOpen] = useState<boolean>(false);
  const { isExpanded } = useSideNav();
  const interceptNavigation = useNavigationInterceptor();
  const [dashboardBillingFlag] = useFeatureFlag('usage-based-billing-on-dashboard', 'off');
  const creditUsageSecretAccess = useCreditUsageSecretAccess();

  // A page with unsaved changes (e.g. the identity editor) may block the
  // navigation and take over with its own confirm dialog.
  const guardedNavigate = (href: string) => (event: { preventDefault: () => void }) => {
    if (interceptNavigation(href)) {
      event.preventDefault();
    }
  };
  const { openModal } = useGlobalModal({
    registeredModals: ['UPGRADE'],
  });

  const handleAddCredits = () => {
    setUsageOpen(false);
    setAddCreditsOpen(true);
  };

  const handleUpgrade = () => {
    openModal?.({
      type: 'UPGRADE',
      trackingSource: 'side-navigation',
    });
  };

  const showUpgradePrompt =
    workspaceType === 'INDIVIDUAL' && (planType === 'FREE' || planType === 'PREMIUM');
  const showBillingAndSubscriptionLink = planType !== 'FREE' && workspaceRole !== 'MEMBER';
  // Credits are a Business concept: a team workspace on the Team plan, seen by an
  // admin or owner. Individual workspaces and the Pro plan are out of scope
  // (docs/usage-billing/credit-lifetime-design.md §3).
  const isCreditUsageEligible =
    workspaceRole !== 'MEMBER' && workspaceType === 'TEAM' && planType === 'TEAM';
  // The secret code is a tester's switch, so it also lifts the workspace and plan
  // restrictions — otherwise the surface can only be demonstrated on a workspace
  // that already qualifies. The role gate holds either way, as does the API's own
  // ADMIN and team-workspace check.
  const showCreditUsage =
    (dashboardBillingFlag === 'on' && isCreditUsageEligible) ||
    (creditUsageSecretAccess && workspaceRole !== 'MEMBER');

  const billingHref = `/${workspaceId}${WORKSPACE_ROUTES.SETTINGS.BILLING_AND_SUBSCRIPTION}`;
  const accountHref = `/${workspaceId}${WORKSPACE_ROUTES.SETTINGS.PERSONAL_DETAILS}`;
  const logoutHref = '/logout';

  const workspaceInitial = workspaceName.charAt(0)?.toUpperCase();

  return (
    <div className={dropDownContainer}>
      <Menu
        opened={isOpen}
        onChange={setIsOpen}
        position="bottom-start"
        shadow="md"
        width={300}
        withinPortal
      >
        <Menu.Target>
          <div style={{ width: '100%' }}>
            {/* Renders when sidenav is collapsed */}
            {!isExpanded && (
              <ContractedButton
                initials={workspaceInitial}
                avatarUrl={workspaceAvatarUrl}
                toggleDropdown={() => setIsOpen((prev) => !prev)}
              />
            )}

            {/* Renders when sidenav is expanded */}
            {isExpanded && (
              <ExpandedButton
                workspaceName={workspaceName}
                initials={workspaceInitial}
                avatarUrl={workspaceAvatarUrl}
                toggleDropdown={() => setIsOpen((prev) => !prev)}
                isOpen={isOpen}
              />
            )}
          </div>
        </Menu.Target>
        <Menu.Dropdown>
          <Box p="md">
            <Group wrap="nowrap" align="center" gap="sm">
              <Avatar
                src={workspaceAvatarUrl}
                alt={workspaceName}
                color="red"
                radius="sm"
                size="md"
                bd="1px solid gray.1"
              >
                {workspaceInitial}
              </Avatar>
              <Stack gap="0">
                <Text fw={600} size="sm">
                  {workspaceName}
                </Text>
                <Text c="dimmed" size="xs">
                  {planDescription}
                </Text>
              </Stack>
            </Group>
          </Box>

          <Divider />
          <Stack py="sm" gap="xs">
            {showUpgradePrompt && (
              <Menu.Item fw="600" onClick={handleUpgrade}>
                Upgrade
              </Menu.Item>
            )}
            {showCreditUsage && (
              // The credits row is a bordered card rather than a plain menu item,
              // so it is inset from the dropdown's edges here.
              <Box px="xs">
                <CreditUsageMenuItem
                  workspaceId={workspaceId}
                  onOpenUsage={() => {
                    setIsOpen(false);
                    setUsageOpen(true);
                  }}
                />
              </Box>
            )}
            {showBillingAndSubscriptionLink && (
              <Menu.Item
                component={Link}
                fw="600"
                href={billingHref}
                onNavigate={guardedNavigate(billingHref)}
              >
                Billing & Subscription
              </Menu.Item>
            )}
            <Menu.Item
              fw="600"
              component={Link}
              href={accountHref}
              onNavigate={guardedNavigate(accountHref)}
            >
              Account
            </Menu.Item>
            <Menu.Item
              fw="600"
              component={Link}
              href={logoutHref}
              onNavigate={guardedNavigate(logoutHref)}
            >
              Logout
            </Menu.Item>
          </Stack>
        </Menu.Dropdown>
      </Menu>
      {showCreditUsage && (
        <CreditUsageOverlay
          workspaceId={workspaceId}
          open={usageOpen}
          onClose={() => setUsageOpen(false)}
          onAddCredits={handleAddCredits}
        />
      )}
      {showCreditUsage && (
        <AddCreditsModal
          workspaceId={workspaceId}
          open={addCreditsOpen}
          onClose={() => setAddCreditsOpen(false)}
        />
      )}
    </div>
  );
}
